import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { ShowingHeatmap } from "@/components/ShowingHeatmap";
import { logger } from "@/lib/logger";

interface ShowingEventWithNorth {
  id: string;
  north_id: string;
  event_type: string;
  title: string;
  scheduled_date: string;
  feedback: string | null;
  created_at: string;
  updated_at: string;
  showing_time_start?: string | null;
  showing_time_end?: string | null;
  status?: string | null;
  source?: string | null;
  agent_name?: string | null;
  agent_company?: string | null;
  // Resolved client-side from northsMap
  norths: {
    id: string;
    address: string;
  };
}

interface NorthInfo {
  id: string;
  address: string;
}

function formatTime(isoString: string | null | undefined): string | null {
  if (!isoString) return null;
  try {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return null;
  }
}

function formatDateHeader(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function getEventDateKey(event: ShowingEventWithNorth): string {
  const ts = event.showing_time_start ?? event.scheduled_date;
  return ts.split("T")[0];
}

function groupByDate(
  events: ShowingEventWithNorth[]
): Map<string, ShowingEventWithNorth[]> {
  return events.reduce((map, ev) => {
    const key = getEventDateKey(ev);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(ev);
    return map;
  }, new Map<string, ShowingEventWithNorth[]>());
}

function SourceBadge({ source }: { source?: string | null }) {
  if (source === "email" || source === "showingtime") {
    return (
      <Badge variant="outline" className="text-xs border-blue-400 text-blue-600">
        Email Synced
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs text-muted-foreground">
      Manual
    </Badge>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  if (!status || status === "confirmed") return null;
  const classMap: Record<string, string> = {
    cancelled: "border-red-400 text-red-500",
    requested: "border-amber-400 text-amber-600",
    completed: "border-blue-400 text-blue-500",
  };
  return (
    <Badge
      variant="outline"
      className={`text-xs ${classMap[status] ?? ""}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

function EventRow({ event }: { event: ShowingEventWithNorth }) {
  const startTime = formatTime(event.showing_time_start);
  const endTime = formatTime(event.showing_time_end);
  const timeDisplay = startTime
    ? endTime
      ? `${startTime} – ${endTime}`
      : startTime
    : new Date(event.scheduled_date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });

  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 py-3 border-b last:border-0">
      {/* Left: type + status badges */}
      <div className="flex flex-wrap items-center gap-1.5 sm:w-40 sm:flex-col sm:items-start sm:gap-1 flex-shrink-0">
        <Badge variant={event.event_type === "showing" ? "default" : "secondary"} className="text-xs">
          {event.event_type === "open_house" ? "Open House" : "Showing"}
        </Badge>
        <StatusBadge status={event.status} />
      </div>

      {/* Center: title + address + agent + feedback */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm leading-snug">{event.title}</p>
        <Link
          to={`/seller-report/${event.norths.id}`}
          className="text-xs text-muted-foreground hover:underline hover:text-foreground transition-colors"
        >
          {event.norths.address}
        </Link>
        {(event.agent_name || event.agent_company) && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {[event.agent_name, event.agent_company].filter(Boolean).join(" · ")}
          </p>
        )}
        {event.feedback && (
          <p className="text-xs text-muted-foreground italic mt-1 line-clamp-1">
            {event.feedback}
          </p>
        )}
      </div>

      {/* Right: time + source */}
      <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1 flex-shrink-0">
        <p className="text-xs text-muted-foreground whitespace-nowrap">{timeDisplay}</p>
        <SourceBadge source={event.source} />
      </div>
    </div>
  );
}

function EventGroup({
  dateKey,
  events,
}: {
  dateKey: string;
  events: ShowingEventWithNorth[];
}) {
  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-2 mb-1">
        <p className="text-sm font-semibold text-foreground">{formatDateHeader(dateKey)}</p>
      </div>
      <div className="bg-card border rounded-lg px-4 divide-y divide-border">
        {events.map((ev) => (
          <EventRow key={ev.id} event={ev} />
        ))}
      </div>
    </div>
  );
}

function EventList({
  events,
  emptyMessage,
  descending = false,
}: {
  events: ShowingEventWithNorth[];
  emptyMessage: string;
  descending?: boolean;
}) {
  if (events.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground text-sm">
        {emptyMessage}
      </div>
    );
  }

  const grouped = groupByDate(events);
  const sortedKeys = Array.from(grouped.keys()).sort();
  if (descending) sortedKeys.reverse();

  return (
    <div className="space-y-6">
      {sortedKeys.map((key) => (
        <EventGroup key={key} dateKey={key} events={grouped.get(key)!} />
      ))}
    </div>
  );
}

export default function Activity() {
  const { getEffectiveUserId } = useImpersonation();

  const [listingFilter, setListingFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  // Fetch team's norths (via user_ids in the team)
  const { data: northsMap } = useQuery({
    queryKey: ["activity-norths-map"],
    queryFn: async (): Promise<Map<string, NorthInfo>> => {
      const userId = await getEffectiveUserId();
      if (!userId) return new Map();

      // Get all user_ids on the team
      const { data: myProfile } = await supabase
        .from("profiles")
        .select("team_id")
        .eq("user_id", userId)
        .maybeSingle();

      if (!myProfile?.team_id) return new Map();

      const { data: teamProfiles } = await supabase
        .from("profiles")
        .select("user_id")
        .eq("team_id", myProfile.team_id);

      const userIds = (teamProfiles ?? []).map((p) => p.user_id);
      if (userIds.length === 0) return new Map();

      const { data: norths, error } = await supabase
        .from("norths")
        .select("id, address")
        .in("user_id", userIds);

      if (error) {
        logger.error("Error fetching norths for Activity:", error);
        return new Map();
      }

      const map = new Map<string, NorthInfo>();
      for (const n of norths ?? []) {
        map.set(n.id, { id: n.id, address: n.address });
      }
      return map;
    },
    staleTime: 1000 * 60 * 5,
  });

  const northIds = useMemo(
    () => (northsMap ? Array.from(northsMap.keys()) : null),
    [northsMap]
  );

  const today = new Date().toISOString().split("T")[0];

  function attachNorths(rows: any[]): ShowingEventWithNorth[] {
    return rows
      .filter((r) => northsMap?.has(r.north_id))
      .map((r) => ({ ...r, norths: northsMap!.get(r.north_id)! }));
  }

  // Upcoming events
  const { data: upcomingRaw, isLoading: upcomingLoading } = useQuery({
    queryKey: ["activity-upcoming", northIds],
    queryFn: async () => {
      if (!northIds || northIds.length === 0) return [];
      const { data, error } = await supabase
        .from("showing_events")
        .select("*")
        .in("north_id", northIds)
        .gte("scheduled_date", today)
        .order("scheduled_date", { ascending: true });
      if (error) {
        logger.error("Error fetching upcoming events:", error);
        return [];
      }
      return attachNorths(data ?? []);
    },
    enabled: !!northIds && northIds.length > 0,
    staleTime: 1000 * 60 * 2,
  });

  // Past events
  const { data: pastRaw, isLoading: pastLoading } = useQuery({
    queryKey: ["activity-past", northIds],
    queryFn: async () => {
      if (!northIds || northIds.length === 0) return [];
      const { data, error } = await supabase
        .from("showing_events")
        .select("*")
        .in("north_id", northIds)
        .lt("scheduled_date", today)
        .order("scheduled_date", { ascending: false });
      if (error) {
        logger.error("Error fetching past events:", error);
        return [];
      }
      return attachNorths(data ?? []);
    },
    enabled: !!northIds && northIds.length > 0,
    staleTime: 1000 * 60 * 2,
  });

  const isLoading = northsMap === undefined || upcomingLoading || pastLoading;

  // Distinct listings from all events for filter dropdown
  const allEvents = useMemo(
    () => [...(upcomingRaw ?? []), ...(pastRaw ?? [])],
    [upcomingRaw, pastRaw]
  );

  const listingOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const ev of allEvents) {
      if (!seen.has(ev.norths.id)) seen.set(ev.norths.id, ev.norths.address);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [allEvents]);

  // Apply client-side filters
  function applyFilters(events: ShowingEventWithNorth[]) {
    return events.filter((ev) => {
      if (listingFilter !== "all" && ev.norths.id !== listingFilter) return false;
      if (typeFilter !== "all" && ev.event_type !== typeFilter) return false;
      if (sourceFilter === "email" && ev.source !== "email" && ev.source !== "showingtime")
        return false;
      if (sourceFilter === "manual" && ev.source !== "manual" && ev.source !== null && ev.source !== "")
        return false;
      return true;
    });
  }

  const upcoming = useMemo(
    () => applyFilters(upcomingRaw ?? []),
    [upcomingRaw, listingFilter, typeFilter, sourceFilter]
  );
  const past = useMemo(
    () => applyFilters(pastRaw ?? []),
    [pastRaw, listingFilter, typeFilter, sourceFilter]
  );

  // Heatmap uses filtered allEvents restricted to listing filter
  const heatmapEvents = useMemo(
    () =>
      allEvents.filter(
        (ev) => listingFilter === "all" || ev.norths.id === listingFilter
      ),
    [allEvents, listingFilter]
  );

  const defaultTab = upcoming.length > 0 || upcomingLoading ? "upcoming" : "past";

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-6 py-8 max-w-5xl">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold">Activity</h1>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Select value={listingFilter} onValueChange={setListingFilter}>
              <SelectTrigger className="h-8 text-xs w-48">
                <SelectValue placeholder="All Listings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Listings</SelectItem>
                {listingOptions.map(([id, address]) => (
                  <SelectItem key={id} value={id}>
                    {address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="showing">Showing</SelectItem>
                <SelectItem value="open_house">Open House</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="h-8 text-xs w-40">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="email">Email Synced</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Heatmap */}
            {allEvents.length > 0 && (
              <div className="mb-6">
                <ShowingHeatmap events={heatmapEvents} />
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue={defaultTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="upcoming">
                  Upcoming{upcomingRaw && upcomingRaw.length > 0 ? ` (${upcoming.length})` : ""}
                </TabsTrigger>
                <TabsTrigger value="past">
                  Past{pastRaw && pastRaw.length > 0 ? ` (${past.length})` : ""}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming">
                <EventList
                  events={upcoming}
                  emptyMessage="No upcoming showings. They'll appear here once scheduled."
                />
              </TabsContent>

              <TabsContent value="past">
                <EventList
                  events={past}
                  emptyMessage="No showing history yet."
                  descending
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
}
